import { QdsDialog } from '../dialog/qds-dialog.js';

/**
 * Edge-aligned native dialog sharing QDS modal, focus and async-action behavior.
 * @element qds-drawer
 * @attr {boolean} open - Requested visibility; use show(invoker) for explicit focus return.
 * @attr {string} label - Title fallback and accessible name; default Dialog. Supply a meaningful title.
 * @attr {'start'|'end'|'left'|'right'|'top'|'bottom'} placement - Logical or physical edge; default end.
 * @attr {'sm'|'md'|'lg'|'full'} size - Panel extent: 20rem, 32rem, 48rem or viewport; default md.
 * @attr {boolean} nonmodal - Leave the surrounding page interactive; default modal.
 * @attr {boolean} no-escape - Prevent Escape dismissal.
 * @attr {boolean} close-on-backdrop - Permit a full pointer gesture on the modal backdrop to dismiss.
 * @attr {boolean} hide-close - Hide the close button; supply another dismissal action.
 * @attr {boolean} busy - Prevent user dismissal during an action.
 * @attr {'body'|'dialog'} scroll - Scroll the body (default) or entire panel.
 * @property {boolean} open - Reflects the open attribute.
 * @property {boolean} busy - Reflects async progress.
 * @property {HTMLElement|null} initialFocus - Optional focus target inside the drawer.
 * @property {HTMLElement|null} returnFocus - Optional logical focus destination after closure.
 * @property {string} returnValue - Last close result.
 * @slot title - Title content, replacing the label fallback.
 * @slot - Main content; arbitrary native/QDS composition.
 * @slot body - Alternative named main-content slot.
 * @slot footer - Persistent actions below the body.
 * @csspart dialog - Native edge-aligned dialog surface.
 * @csspart title - Heading container.
 * @csspart body - Scrollable content.
 * @csspart footer - Footer actions.
 * @csspart close - Close drawer button.
 * @fires open - Drawer has opened.
 * @fires {{reason: string; returnValue: string}} before-close - Cancelable user close request.
 * @fires {{reason: string; returnValue: string}} close - Drawer closed.
 * @fires {{error: unknown}} action-error - Async action failed; the drawer remains open.
 */
export class QdsDrawer extends QdsDialog {
  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'placement', 'size']; }
  constructor() {
    super();
    const style = document.createElement('style');
    style.textContent = `
      :host { --_drawer-extent: 32rem; }
      :host([size=sm]) { --_drawer-extent: 20rem; }
      :host([size=lg]) { --_drawer-extent: 48rem; }
      dialog { position: fixed; inset: 0; margin: 0; margin-inline-start: auto;
        width: min(var(--qds-drawer-width, var(--_drawer-extent)), 100vw); height: 100dvh;
        max-width: 100vw; max-height: 100dvh; border-radius: var(--qds-drawer-radius, 0); }
      :host([placement=start]) dialog { margin-inline-start: 0; margin-inline-end: auto; }
      :host([placement=left]) dialog { margin: 0; left: 0; right: auto; }
      :host([placement=right]) dialog { margin: 0; left: auto; right: 0; }
      :host([placement=top]) dialog, :host([placement=bottom]) dialog {
        width: 100vw; height: min(var(--qds-drawer-height, var(--_drawer-extent)), 100dvh); }
      :host([placement=bottom]) dialog { margin-block-start: auto; }
      :host([size=full]) dialog { width: 100vw; height: 100dvh; }
      .body { flex: 1 1 auto; }
      :host([scroll=dialog]) .body { flex: 0 0 auto; }
      @media (forced-colors: active) { dialog { border: 1px solid CanvasText; } }
    `;
    this.shadowRoot!.append(style);
    this.shadowRoot!.querySelector('[part=close]')!.setAttribute('aria-label', 'Close drawer');
  }
  get placement(): 'start' | 'end' | 'left' | 'right' | 'top' | 'bottom' {
    const value = this.getAttribute('placement');
    return value === 'start' || value === 'left' || value === 'right' || value === 'top' || value === 'bottom' ? value : 'end';
  }
  set placement(value: 'start' | 'end' | 'left' | 'right' | 'top' | 'bottom') { this.setAttribute('placement', value ?? 'end'); }
  get size(): 'sm' | 'md' | 'lg' | 'full' {
    const value = this.getAttribute('size');
    return value === 'sm' || value === 'lg' || value === 'full' ? value : 'md';
  }
  set size(value: 'sm' | 'md' | 'lg' | 'full') { this.setAttribute('size', value ?? 'md'); }
  override connectedCallback(): void {
    // Two local upgrades avoid loading the shared form-control chunk for an overlay.
    for (const name of ['placement', 'size'] as const) {
      if (Object.prototype.hasOwnProperty.call(this, name)) {
        const value = this[name];
        Reflect.deleteProperty(this, name);
        Reflect.set(this, name, value);
      }
    }
    super.connectedCallback();
  }
  /** Open and remember an optional logical invoker. Defaults to modal. */
  override show(invoker?: HTMLElement): void { super.show(invoker); }
  /** Programmatic closure; requestClose() offers cancelable user dismissal. */
  override close(value: string = '', reason: 'api' | 'escape' | 'backdrop' | 'close-button' | 'action' | 'attribute' | 'native' | 'disconnect' = 'api'): void { super.close(value, reason); }
  override requestClose(reason: 'api' | 'escape' | 'backdrop' | 'close-button' | 'action' | 'attribute' | 'native' | 'disconnect' = 'api', value: string = ''): boolean { return super.requestClose(reason, value); }
  /** Share QDS async cancellation, errors, busy state and disconnect abortion. */
  override runAction(action: (signal: AbortSignal) => unknown | Promise<unknown>, value: string = 'confirm'): Promise<boolean> { return super.runAction(action, value); }
}
if (!customElements.get('qds-drawer')) customElements.define('qds-drawer', QdsDrawer);
